import { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  Menu,
  X,
  Sun,
  Moon,
  LayoutDashboard,
  LogOut,
  Users,
  Image,
  Camera,
  Car,
  ClipboardList,
} from "lucide-react";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { auth, db } from "../firebase";
import logo from "../assets/logo.png";

export default function Navbar() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [dark, setDark] = useState(localStorage.getItem("theme") !== "light");
  const [admin, setAdmin] = useState(false);

  /* ================= THEME ================= */
  const toggleTheme = () => {
    const next = !dark;
    setDark(next);
    localStorage.setItem("theme", next ? "dark" : "light");
    window.dispatchEvent(new Event("theme-change"));
  };

  useEffect(() => {
    const stored = localStorage.getItem("theme");
    if (stored) setDark(stored !== "light");
  }, []);

  /* ================= AUTH ================= */
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setAdmin(false);
        return;
      }
      const snap = await getDoc(doc(db, "users", user.uid));
      setAdmin(snap.exists() && snap.data().approved === true);
    });
    return () => unsub();
  }, []);

  const logout = async () => {
    await signOut(auth);
    navigate("/admin/login");
  };

  const theme = {
    header: dark
      ? "bg-neutral-950 text-white border-neutral-800"
      : "bg-white text-gray-900 border-gray-200",

    desktopLink: (active) =>
      active
        ? "bg-gray-900 text-white dark:bg-white dark:text-black"
        : "hover:bg-gray-100 dark:hover:bg-neutral-800",

    mobileBg: dark
      ? "bg-neutral-950 text-white"
      : "bg-white text-gray-900",
  };

  return (
    <header className={`sticky top-0 z-50 border-b ${theme.header}`}>
      <nav className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
        {/* LOGO */}
        <NavLink to={admin ? "/admin/dashboard" : "/"} className="flex items-center gap-2">
          <img
  src={logo}
  alt="Deonyak Hearse Service logo"
  className="h-8 w-8"
/>

          <span className="font-semibold text-lg tracking-tight">
            Deonyak Hearse Service
          </span>
        </NavLink>

        {/* DESKTOP */}
        <div className="hidden md:flex items-center gap-1">
          {!admin ? (
            <>
              <DesktopLink to="/">Home</DesktopLink>
              <DesktopLink to="/shop">Shop</DesktopLink>
              <DesktopLink to="/book">Book Hearse</DesktopLink>
              <DesktopLink to="/cars">Cars</DesktopLink>
              <DesktopLink to="/gallery">Gallery</DesktopLink>
              <DesktopLink to="/contact">Contact</DesktopLink>
            </>
          ) : (
            <>
              <AdminBtn to="/admin/dashboard" icon={LayoutDashboard} label="Dashboard" />
              <AdminBtn to="/admin/customers" icon={ClipboardList} label="Customers" />
              <AdminBtn to="/admin/users" icon={Users} label="Users" />
              <AdminBtn to="/admin/gallery" icon={Image} label="Gallery" />
              <AdminBtn to="/admin/bookings" icon={ClipboardList} label="Bookings" />
              <AdminBtn to="/admin/image-uploader" icon={Camera} label="Image2Url" />
              <AdminBtn to="/admin/cars" icon={Car} label="Cars" />

              <button
                onClick={logout}
                className="ml-2 px-3 py-2 rounded-md bg-red-600 text-white text-sm flex items-center gap-1"
              >
                <LogOut size={16} /> Logout
              </button>
            </>
          )}

          <ThemeBtn dark={dark} toggle={toggleTheme} />
        </div>

        {/* MOBILE BUTTON */}
        <button onClick={() => setOpen(true)} className="md:hidden">
          <Menu size={24} />
        </button>
      </nav>

      {/* ================= MOBILE ENTERPRISE DRAWER ================= */}
      {open && (
        <>
          <div
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-40 bg-black/70"
          />

          <aside
            className={`fixed right-0 top-0 z-50 h-full w-[90%] max-w-sm
            ${theme.mobileBg}
            border-l border-neutral-800
            shadow-2xl`}
          >
            {/* HEADER */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-800">
              <div className="flex items-center gap-2">
                <img
  src={logo}
  alt="Deonyak Hearse Service logo"
  className="h-8 w-8"
/>

                <span className="font-semibold">Deonyak Hearse</span>
              </div>
              <button onClick={() => setOpen(false)}>
                <X size={22} />
              </button>
            </div>

            {/* CONTENT */}
            <div className="px-4 py-4 space-y-4 flex flex-col">

              {/* THEME */}
              <div className="flex justify-between items-center p-4 rounded-md border border-neutral-800">
                <span className="text-sm font-medium">Appearance</span>
                <ThemeBtn dark={dark} toggle={toggleTheme} />
              </div>

              {/* NAV */}
              {!admin ? (
                <NavSection title="Main Menu">
                  <MobileLink to="/" label="Home" close={setOpen} />
                  <MobileLink to="/shop" label="Shop" close={setOpen} />
                  <MobileLink to="/book" label="Book Hearse" close={setOpen} />
                  <MobileLink to="/cars" label="Our Cars" close={setOpen} />
                  <MobileLink to="/gallery" label="Gallery" close={setOpen} />
                  <MobileLink to="/contact" label="Contact" close={setOpen} />
                </NavSection>
              ) : (
                <NavSection title="Administration">
                  <MobileLink to="/admin/dashboard" label="Dashboard" close={setOpen} />
                  <MobileLink to="/admin/customers" label="Customers" close={setOpen} />
                  <MobileLink to="/admin/users" label="Users" close={setOpen} />
                  <MobileLink to="/admin/gallery" label="Gallery" close={setOpen} />
                  <MobileLink to="/admin/bookings" label="Bookings" close={setOpen} />
                  <MobileLink to="/admin/image-uploader" label="Image2Url" close={setOpen} />
                  <MobileLink to="/admin/cars" label="Cars" close={setOpen} />

                  <button
                    onClick={logout}
                    className="mt-4 w-full px-4 py-3 rounded-md bg-red-600 text-white text-sm"
                  >
                    Logout
                  </button>
                </NavSection>
              )}
            </div>
          </aside>
        </>
      )}
    </header>
  );
}

/* ================= COMPONENTS ================= */

function DesktopLink({ to, children }) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `px-3 py-2 rounded-md text-sm ${isActive ? "bg-gray-900 text-white dark:bg-white dark:text-black" : ""}`
      }
    >
      {children}
    </NavLink>
  );
}

function AdminBtn({ to, icon: Icon, label }) {
  return (
    <NavLink
      to={to}
      className="flex items-center gap-1 px-3 py-2 rounded-md bg-neutral-800 text-white text-sm"
    >
      <Icon size={16} /> {label}
    </NavLink>
  );
}

function MobileLink({ to, label, close }) {
  return (
    <NavLink
      to={to}
      onClick={() => close(false)}
      className="block px-4 py-3 rounded-md text-sm hover:bg-neutral-800"
    >
      {label}
    </NavLink>
  );
}

function NavSection({ title, children }) {
  return (
    <div className="flex flex-col">
      <p className="mb-2 text-xs uppercase tracking-widest text-gray-400">
        {title}
      </p>

      {/* tighter vertical spacing */}
      <div className="flex flex-col gap-1">
        {children}
      </div>
    </div>
  );
}


function ThemeBtn({ dark, toggle }) {
  return (
    <button
      onClick={toggle}
      className="flex items-center gap-2 px-3 py-2 rounded-md border border-neutral-700"
    >
      {dark ? <Sun size={16} /> : <Moon size={16} />}
      <span className="text-sm">{dark ? "Light" : "Dark"}</span>
    </button>
  );
}
